import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, memo } from 'react';

const FormSubmitOnBlurComponent = ({
  children,
  onComponentBlur,
  onSubmit,
  timeout = 100,
  focusOnClick = true,
  onFocus,
  onClick,
  ...rest
}) => {
  const focusedRef = useRef(false);
  const timeoutRef = useRef(null);
  const formRef = useRef(null);

  const handleClickOutside = useCallback(
    event => {
      if (!focusedRef.current) return;

      focusedRef.current = false;

      timeoutRef.current = setTimeout(() => {
        (onComponentBlur || onSubmit)(event, formRef.current);
      }, timeout);
    },
    [onComponentBlur, onSubmit, timeout],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleFocus = useCallback(
    (...args) => {
      focusedRef.current = true;
      clearTimeout(timeoutRef.current);

      return onFocus?.(...args);
    },
    [onFocus],
  );

  const handleClick = useCallback(
    (...args) => {
      if (focusOnClick) {
        focusedRef.current = true;
        clearTimeout(timeoutRef.current);
      }

      return onClick?.(...args);
    },
    [focusOnClick, onClick],
  );

  useEffect(
    () => () => {
      clearTimeout(timeoutRef.current);
    },
    [],
  );

  return (
    <form {...rest} ref={formRef} onClick={handleClick} onFocus={handleFocus}>
      {children}
    </form>
  );
};

FormSubmitOnBlurComponent.propTypes = {
  children: PropTypes.node.isRequired,
  focusOnClick: PropTypes.bool,
  onComponentBlur: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  timeout: PropTypes.number,
};

export default memo(FormSubmitOnBlurComponent);
